import {
  Stack,
  Text,
  showToast,
  PrimaryButton,
  BasicButton,
} from "@certn/cascadia";
import {
  TextInputFormikField,
  ButtonGroup,
  SelectFormikField,
  SelectItem,
  SectionMessage,
  mq,
} from "@certn/cascadia-react";
import { useTranslation } from "react-i18next";
import { useSubmit } from "@certn/api";
import { useState } from "react";
import { usePermissions } from "../../../components/permissions/PermissionsProvider";
import { useClientAccount } from "../../../hooks/useClientAccount";
import { Formik, Form } from "formik";
import { object, number, string } from "yup";
import { css } from "@emotion/css";
import {
  convertValueToUnit,
  getInitialFormValues,
  DAYS_PER_YEAR,
  MIN_DAYS,
  MAX_DAYS,
  RetentionUnit,
  RETENTION_UNITS,
} from "./dataRetentionUtils";

export function DataRetentionConfig() {
  const { t } = useTranslation();
  const RETENTION_PERIOD_MIN_ERROR_MESSAGE = t(
    "Minimum retention is 90 days. Please contact your CSM for legal review if shorter periods are required."
  );
  const RETENTION_PERIOD_MAX_ERROR_MESSAGE = t(
    "Retention beyond 3 years requires legal justification. Please contact your CSM to request extended retention."
  );
  const { hasPermission } = usePermissions();
  const [pendingAction, setPendingAction] = useState<"save" | "reset" | null>(
    null
  );
  const canConfigureDataRetention = hasPermission({
    action: "configure",
    target: "data_retention",
    scope: "all",
  });
  const { client, mutate, isLoading } = useClientAccount();
  const { submit: updateDataRetention, isSubmitting: isUpdatingDataRetention } =
    useSubmit(
      "/api/internal/client-portal/customer-accounts/data-retention/update/",
      {
        showToastOnError: true,
        onSuccess: async () => {
          showToast(t("Data retention setting updated"), {
            type: "success",
          });
          await mutate();
          setPendingAction(null);
        },
        onFailure: () => {
          setPendingAction(null);
        },
      }
    );

  const unitLabels = {
    days: t("Days"),
    years: t("Years"),
  } satisfies Record<RetentionUnit, string>;

  const unitOptions = RETENTION_UNITS.map((unit) => ({
    id: unit,
    label: unitLabels[unit],
  }));

  const validationSchema = object().shape({
    retention_unit: string()
      .oneOf([...RETENTION_UNITS])
      .required(),
    retention_value_days: number()
      .typeError(t("Please enter a valid number"))
      .integer(t("Please enter a whole number"))
      .nullable()
      .when("retention_unit", {
        is: "days",
        then: (schema) =>
          schema
            .required(t("This field is required"))
            .min(MIN_DAYS, RETENTION_PERIOD_MIN_ERROR_MESSAGE)
            .max(MAX_DAYS, RETENTION_PERIOD_MAX_ERROR_MESSAGE),
        otherwise: (schema) => schema.nullable(),
      }),
    retention_value_years: number()
      .typeError(t("Please enter a valid number"))
      .integer(t("Please enter a whole number"))
      .nullable()
      .when("retention_unit", {
        is: "years",
        then: (schema) =>
          schema
            .required(t("This field is required"))
            .min(
              Math.ceil(MIN_DAYS / DAYS_PER_YEAR),
              RETENTION_PERIOD_MIN_ERROR_MESSAGE
            )
            .max(
              Math.floor(MAX_DAYS / DAYS_PER_YEAR),
              RETENTION_PERIOD_MAX_ERROR_MESSAGE
            ),
        otherwise: (schema) => schema.nullable(),
      }),
  });

  const initialValues = getInitialFormValues({
    days: client?.custom_case_deletion,
  });

  return (
    <Stack gap="space-5">
      <Formik
        initialValues={initialValues}
        onSubmit={(values) => {
          setPendingAction("save");
          const currentValue =
            values[`retention_value_${values.retention_unit}`];
          const daysValue = convertValueToUnit({
            value: currentValue,
            fromUnit: values.retention_unit,
            toUnit: "days",
          });
          updateDataRetention({
            custom_case_deletion: daysValue,
          });
        }}
        validationSchema={validationSchema}
        enableReinitialize
      >
        {({ dirty, isValid, setFieldValue, values }) => (
          <Form noValidate>
            <Stack gap="space-5">
              <Text style="global/heading-large">
                {t("Applicant data retention")}
              </Text>
              <SectionMessage type="info" icon="info">
                {t(
                  "Configure applicant data retention. As the Data Controller, you define retention based on your legal basis. Certn (Data Processor) enforces this for all cases, including historical ones. Older cases will be deleted."
                )}
              </SectionMessage>
              <Text color="text-weak" style="input/helper">
                {t(
                  "Applicant personal data (PII) will be automatically deleted after the specified retention period for all applicants processed through your account."
                )}
              </Text>
              <div className={styles.inputRow}>
                <div className={styles.inputFields}>
                  <div className={styles.inputContainer}>
                    {values.retention_unit === "days" ? (
                      <TextInputFormikField
                        label={t("Retention period")}
                        name="retention_value_days"
                        type="number"
                        testID="data-retention-value-days-input"
                        isRequired
                        isDisabled={isLoading || !canConfigureDataRetention}
                      />
                    ) : (
                      <TextInputFormikField
                        label={t("Retention period")}
                        name="retention_value_years"
                        type="number"
                        testID="data-retention-value-years-input"
                        isRequired
                        isDisabled={isLoading || !canConfigureDataRetention}
                      />
                    )}
                  </div>
                  <div className={styles.unitSelect}>
                    <SelectFormikField
                      label={t("Unit")}
                      isRequired
                      name="retention_unit"
                      testID="data-retention-unit-select"
                      isDisabled={isLoading || !canConfigureDataRetention}
                      onSelectionChange={(newUnit) => {
                        const oldUnit = values.retention_unit;
                        const oldValue = `retention_value_${oldUnit}` as const;
                        setFieldValue(oldValue, null);
                        const converted = convertValueToUnit({
                          value: values[oldValue],
                          fromUnit: oldUnit,
                          toUnit: newUnit as RetentionUnit,
                        });
                        if (converted)
                          setFieldValue(
                            `retention_value_${newUnit}`,
                            converted
                          );
                      }}
                    >
                      {unitOptions.map((option) => (
                        <SelectItem key={option.id} id={option.id}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectFormikField>
                  </div>
                </div>
                <div className={styles.buttonGroup}>
                  <ButtonGroup>
                    <BasicButton
                      isDisabled={
                        isLoading ||
                        !canConfigureDataRetention ||
                        isUpdatingDataRetention
                      }
                      isLoading={pendingAction === "reset"}
                      onPress={() => {
                        setPendingAction("reset");
                        updateDataRetention({
                          custom_case_deletion: null,
                        });
                      }}
                      testID="data-retention-reset-button"
                    >
                      {t("Reset to default")}
                    </BasicButton>
                    <PrimaryButton
                      type="submit"
                      isDisabled={
                        isLoading ||
                        !canConfigureDataRetention ||
                        !dirty ||
                        !isValid ||
                        isUpdatingDataRetention
                      }
                      isLoading={pendingAction === "save"}
                      testID="data-retention-save-button"
                    >
                      {t("Save changes")}
                    </PrimaryButton>
                  </ButtonGroup>
                </div>
              </div>
            </Stack>
          </Form>
        )}
      </Formik>
    </Stack>
  );
}

const styles = {
  inputRow: css({
    display: "flex",
    flexWrap: "wrap",
    gap: "var(--space-3)",
    alignItems: "flex-start",
  }),
  inputFields: css({
    display: "flex",
    gap: "var(--space-3)",
  }),
  inputContainer: css({
    width: "160px",
  }),
  unitSelect: css({
    width: "120px",
  }),
  buttonGroup: css(
    { display: "flex", alignItems: "flex-end" },
    mq({ width: ["100%", "auto"], height: ["auto", "64px"] })
  ),
};
